import { debounce } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { useListNavigationContext } from './listNavigation/ListNavigationContext';

function computeFetchOffset(scrollOffset, recordHeight, columns) {
  return Math.floor((scrollOffset / recordHeight) + 1) * columns;
}

export const useScrollFetchData = (setOffset = null, setFetched, filteredCount, recordHeight = 380, pageOffset = 215, columns = 1) => {
  const prevScrollTop = useRef(0);
  const listNavigation = useListNavigationContext();
  const persistRef = useRef(listNavigation?.persistScroll);
  persistRef.current = listNavigation?.persistScroll;

  const paramsRef = useRef({ setOffset, setFetched, filteredCount, recordHeight, pageOffset, columns });
  paramsRef.current = { setOffset, setFetched, filteredCount, recordHeight, pageOffset, columns };

  const persistNow = useCallback((scrollOffset) => {
    const { recordHeight: height, columns: cols } = paramsRef.current;
    persistRef.current?.({
      scrollOffset,
      fetchOffset: computeFetchOffset(scrollOffset, height, cols),
    });
  }, []);

  const debouncedFetchRef = useRef(null);
  if (!debouncedFetchRef.current) {
    debouncedFetchRef.current = debounce((scrollOffset) => {
      const {
        setOffset: setOff,
        setFetched,
        filteredCount: count,
        recordHeight: height,
        pageOffset: inset,
        columns: cols,
      } = paramsRef.current;
      const newOffset = computeFetchOffset(scrollOffset, height, cols);
      setOff?.(newOffset);
      const newFetchedValue = newOffset + Math.floor((window.innerHeight - inset) / height) * cols;
      setFetched(Math.min(newFetchedValue, count));
    }, 300);
  }

  useEffect(() => () => {
    persistNow(prevScrollTop.current);
    debouncedFetchRef.current.flush();
    debouncedFetchRef.current.cancel();
  }, [persistNow]);

  return useCallback(({ scrollOffset }) => {
    persistNow(scrollOffset);
    if (scrollOffset !== prevScrollTop.current) {
      prevScrollTop.current = scrollOffset;
      debouncedFetchRef.current(scrollOffset);
    }
  }, [persistNow]);
};
